import { useMutation, useQuery } from '@tanstack/react-query'
import AXIOS_INSTANCE from '../api/axiosInstance'
import { queryClient } from '../store/provider/TanStackProvider'
import { LabOrderCreatePayload, LabOrderUpdatePayload, LabResMock, SingleLabOrderRes } from '../types/lab.type'

type LabTypes = {
  patientId?: string
  id?: string
  created_at?: string
  status?: 'pending' | 'completed' | 'cancelled' | 'in_progress'
  limit?: string
  page?: string
  sortDir?: string
  query?: string
  encounterId?: string
}
export const useLab = (arg?: LabTypes) => {
  const { created_at, encounterId, id, limit, page, patientId, query } = arg ?? {}
  const { sortDir, status } = arg ?? {}
  const { data: orders, isLoading } = useQuery({
    queryFn: async () => {
      const { data } = await AXIOS_INSTANCE.get<LabResMock>('/lab_orders', {
        params: {
          ...(patientId && { patient_id: patientId }),
          ...(encounterId && { encounter_id: encounterId }),
          ...(status && { status }),
          ...(sortDir && { sort_dir: sortDir }),
          ...(query && { query }),
          ...(limit && { 'page[size]': limit }),
          ...(page && { 'page[number]': page }),
          ...(created_at && { created_at })
        }
      })
      return data
    },
    queryKey: ['lab_order', { created_at, encounterId, limit, page, patientId, query, sortDir, status }]
  })

  const { data: singleLabOrder, isLoading: singleLabOrderIsLoading } = useQuery({
    enabled: !!id,
    queryFn: async () => {
      const { data } = await AXIOS_INSTANCE.get<SingleLabOrderRes>(`/lab_orders/${id}`)
      return data
    },
    queryKey: ['lab_order', { id }]
  })

  const { mutateAsync: createLab } = useMutation({
    mutationFn: async (payload: LabOrderCreatePayload) => {
      const { data } = await AXIOS_INSTANCE.post('/lab_orders', payload, {
        headers: { 'Content-Type': 'application/vnd.api+json' }
      })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lab_order'] })
    }
  })

  /** Pending bundle only — `PATCH /lab_orders/:id`. */
  const { mutateAsync: updateLabOrder } = useMutation({
    mutationFn: async ({ formVal, id: orderId }: { formVal: LabOrderUpdatePayload; id: string }) => {
      const { data } = await AXIOS_INSTANCE.patch(`/lab_orders/${orderId}`, formVal, {
        headers: { 'Content-Type': 'application/vnd.api+json' }
      })
      return data
    },
    onSuccess: (_data, { id: orderId }) => {
      queryClient.invalidateQueries({ queryKey: ['lab_order', { id: orderId }] })
      queryClient.invalidateQueries({ queryKey: ['lab_order'] })
    }
  })

  const { mutateAsync: updateLab } = useMutation({
    mutationFn: async ({ formVal, id }: { formVal: any; id: string }) => {
      const { data } = await AXIOS_INSTANCE.patch(`orders/${id}`, formVal, {
        headers: { 'Content-Type': 'application/vnd.api+json' }
      })
      if (data.data.relationships.orderable.data.type === 'lab_test') {
        return data
      }
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['lab_order', { id }] })
  })

  const { mutateAsync: deleteLab } = useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      await AXIOS_INSTANCE.delete(`orders/${id}`)
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['lab_order'] })
  })

  const { mutateAsync: cancelAllOrder } = useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      await AXIOS_INSTANCE.patch(
        `lab_orders/${id}/cancel`,
        { data: { type: 'lab_order', attributes: { cancellation_reason: 'cancelled' } } },
        {
          headers: { 'Content-Type': 'application/vnd.api+json' }
        }
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lab_order', { id }] })
      queryClient.invalidateQueries({ queryKey: ['lab_order'] })
    }
  })

  const { mutateAsync: completeLabOrder } = useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      const { data } = await AXIOS_INSTANCE.patch(
        `lab_orders/${id}/complete`,
        { data: { type: 'lab_order' } },
        { headers: { 'Content-Type': 'application/vnd.api+json' } }
      )
      return data
    },
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['lab_order', { id }] })
      queryClient.invalidateQueries({ queryKey: ['lab_order'] })
    }
  })

  return {
    cancelAllOrder,
    completeLabOrder,
    createLab,
    deleteLab,
    included: orders?.included,
    singleLabOrder,
    singleLabOrderIsLoading,
    isLoading,
    lab_orders: orders?.data ?? [],
    orders,
    updateLab,
    updateLabOrder
  }
}
